import { type ComponentProps, type ForwardRefExoticComponent, type RefAttributes, useRef, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'

import { Button } from '@/components/ui/button.tsx'
import { Card, CardContent } from '@/components/ui/card.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import '@/components/markdown.scss'

import { EditableComponent } from '@/components/EditableComponent.tsx'
import { MarkdownViewer } from '@/components/MarkdownViewer.tsx'
import { DropdownItem } from '@/components/form/DropdownItem.tsx'
import { BUG_REPORT_TEMPLATE, USER_STORY_TEMPLATE } from '@/constants/DESCRIPTION_TEMPLATES.ts'
import { useMutateStory } from '@/requests/mutations/useMutateStory.ts'
import { metaEnterSubmission } from '@/utils/keyboard.ts'

type Props = {
  storyId: string
  value: string
  switchEditMode: () => void
}

export const Description = ({ storyId, value, switchEditMode }: Props) => {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`description_${storyId}`} variant="title">
        Description
      </Label>
      {/*biome-ignore lint/a11y/useKeyWithClickEvents: */}
      <div
        id={`description_${storyId}`}
        className="py-2 px-3 bg-surface-backgroundElevated border-border-default-neutral rounded-xs"
        onClick={() => {
          switchEditMode()
          setTimeout(() => document.getElementById(`description_${storyId}`)?.focus())
        }}
      >
        {value.trim().length > 0 ? (
          <MarkdownViewer value={value} />
        ) : (
          <div className="text-contents-placeholder font-EN-KR-body2-regular h-18">Add a description</div>
        )}
      </div>
    </div>
  )
}

type EditableVersionProps = {
  storyId: string
  value?: string
  placeholder: string
  switchViewMode: () => void
}

type DescriptionEditMode = 'write' | 'preview'
export const EditableDescription = ({
  storyId,
  value: _value = '',
  placeholder,
  switchViewMode,
}: EditableVersionProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [mode, setMode] = useState<DescriptionEditMode>('write')
  const [value, setValue] = useState(_value)
  const [showTemplates, setShowTemplates] = useState(false)
  const { mutate } = useMutateStory(storyId)

  const focusTextAreaAtEnd = () => {
    const len = textAreaRef.current?.value.length ?? 0
    textAreaRef.current?.focus()
    textAreaRef.current?.setSelectionRange(len, len)
  }

  const addTemplateDesc = (template: string) => {
    setValue((prev) => {
      if (prev?.trim().length === 0) {
        return `${prev}${template}`
      }
      return `${prev}\n${template}`
    })
    setShowTemplates(false)
    setTimeout(() => {
      focusTextAreaAtEnd()
    })
  }

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`description_${storyId}`} variant="title">
        Description
      </Label>
      <Card
        className={cn(
          'border-border-default-neutral  bg-surface-backgroundElevated rounded-xs overflow-hidden',
          mode === 'write' ? 'focus-within:border-border-default-focused' : '',
        )}
      >
        <Tabs
          defaultValue="write"
          className="w-full"
          onValueChange={(v) => {
            setMode(v as DescriptionEditMode)
          }}
        >
          <TabsList className="bg-tb-grey-100 w-full justify-start h-auto p-0 rounded-none">
            <TabsTrigger
              value="write"
              className="px-2.5 py-1.5 font-EN-KR-body2-regular data-[state=active]:font-EN-KR-body2-bold data-[state=active]:bg-surface-backgroundElevated data-[state=active]:shadow-none rounded-none"
            >
              <span className={'text-contents-primary'}>Write</span>
            </TabsTrigger>
            <TabsTrigger
              value="preview"
              className="px-2.5 py-1.5 font-EN-KR-body2-regular data-[state=active]:font-EN-KR-body2-bold data-[state=active]:bg-surface-backgroundElevated data-[state=active]:shadow-none rounded-none"
            >
              <span className={'text-contents-primary'}>Preview</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="write" className="m-0">
            <CardContent className="p-0">
              <div className="px-3 py-2">
                <TextareaAutosize
                  id={`description_${storyId}`}
                  ref={textAreaRef}
                  placeholder={placeholder}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) =>
                    metaEnterSubmission(e, () => {
                      mutate({ description: value }, {})
                      switchViewMode()
                    })
                  }
                  cacheMeasurements
                  className="p-0 w-full min-h-[100px] focus:outline-none rounded-none border-0 font-EN-KR-body2-regular text-contents-primary placeholder:text-contents-placeholder resize-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </CardContent>
          </TabsContent>

          <TabsContent value="preview" className="m-0">
            <CardContent className="p-3">
              <MarkdownViewer value={value} />
            </CardContent>
          </TabsContent>

          <div className="flex items-center justify-between px-2 h-12">
            <div className="flex gap-0.5">
              <Popover open={showTemplates} onOpenChange={(v) => setShowTemplates(v)}>
                {/* todo: temporary styling <start> */}
                <PopoverTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" disabled={mode === 'preview'}>
                    <img src="/images/description-story-template-icon.svg" alt="load template" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="flex flex-col items-start w-auto px-0 py-1 rounded-xs bg-surface-componentBackground"
                >
                  <p className="py-1.5 px-2 h-[26px] text-contents-disabled font-EN-KR-caption-regular">
                    Select a template
                  </p>
                  <DropdownItem displayText="Bug Report" onClick={() => addTemplateDesc(BUG_REPORT_TEMPLATE)} />
                  <DropdownItem displayText="User Story" onClick={() => addTemplateDesc(USER_STORY_TEMPLATE)} />
                </PopoverContent>
                {/* todo: temporary styling <end> */}
              </Popover>
            </div>

            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                className="rounded-xs w-[50px] h-8 px-[3px] py-1 font-EN-KR-body2-regular text-contents-primary"
                onClick={() => switchViewMode()}
              >
                Cancel
              </Button>
              <Button
                data-update-onformclose
                type="button"
                variant="secondary"
                className="w-[111px] rounded-xs px-[3px] py-1 h-8 font-EN-KR-body2-bold"
                onClick={() => {
                  mutate({ description: value }, {})
                  switchViewMode()
                }}
              >
                Add description
              </Button>
            </div>
          </div>
        </Tabs>
      </Card>
    </div>
  )
}

export const TypedEditableDescription = EditableComponent as unknown as ForwardRefExoticComponent<
  EditableProps<string, ComponentProps<typeof Description>, ComponentProps<typeof EditableDescription>> &
    RefAttributes<EditableCommands>
>
